/* eslint-disable react/no-danger */
/* eslint-disable prettier/prettier */
import { EyeOutlined, SendOutlined } from '@ant-design/icons';
import { Card, Modal, Table, Tag, Typography } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { FaFilter, FaSyncAlt } from 'react-icons/fa';
import { v4 } from 'uuid';
import LogInteractionsFilters from '../../../components/LogInteractionsFilters';
import TableLog from '../../../components/TableLog';
import { useApi } from '../../../hooks/api';
import { ILogInteractionRequest } from '../../../interfaces/logInteractionRequest';
import { DEFAULT_PAGEABLE, IPageable } from '../../../interfaces/pageable';
import { IActionHeaderConfig, ITableColumn } from '../../../interfaces/table';
import { ILogWebhookRequest } from '../../../interfaces/logWebhookRequest';

const { Text, Title } = Typography;

const STATUS_TRANSLATIONS: { [key: string]: string } = {
  'sent': 'Enviado',
  'delivered': 'Entregue',
  'read': 'Lido',
  'failed': 'Falha',
  'pending': 'Pendente',
  'processing': 'Processando',
  'queued': 'Na Fila',
};

const LogFullInteraction: React.FC = () => {
  const { api } = useApi();
  const [loading, setLoading] = useState(false);
  const [interactionsRequests, setInteractionsRequests] = useState<
    ILogInteractionRequest[]
  >([]);
  const [webhooksRequests, setWebhooksRequests] = useState<
    ILogWebhookRequest[]
  >([]);
  const [pageable, setPageable] = useState<IPageable>(DEFAULT_PAGEABLE);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [statusModal, setStatusModal] = useState(false);
  const [selectedLogData, setSelectedLogData] = useState<
    ILogInteractionRequest | null
  >(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    media: '',
    broker: '',
    client: '',
    integration: '',
    request_sent_at: '',
    origin: '',
    request_id: '',
    destination: '',
  });

  const statusPriority: { [key: string]: number } = {
    'sent': 1,
    'delivered': 2,
    'read': 3,
  };

  const loadStatus = useCallback(async (external_id: string) => {
    setLoading(true);
    try {
      const response = await api.get(`/logs/webhook-status?external_id=${external_id}`);
      const sortedData = response.data.sort((a: { status: string; }, b: { status: string; }) => {
        const statusA = a.status?.toLowerCase();
        const statusB = b.status?.toLowerCase();

        const priorityA = statusPriority[statusA] || 999;
        const priorityB = statusPriority[statusB] || 999;

        return priorityA - priorityB;
      });
      setWebhooksRequests(sortedData);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  }, []); 

  const loadLogs = useCallback(
    async (
      pageNumber = 1,
      media = filters.media,
      broker = filters.broker,
      client = filters.client,
      integration = filters.integration,
      request_sent_at = filters.request_sent_at,
      origin = filters.origin,
      request_id = filters.request_id,
      destination = filters.destination,
    ) => {
      setLoading(true);
      try {
        const response = await api.get('/logs/interaction-request/list-logs', {
          params: {
            page: pageNumber - 1,
            media,
            broker,
            client,
            integration,
            request_sent_at,
            origin,
            request_id,
            destination
          },
        });
        setInteractionsRequests(response.data.data);
        setPageable(response.data);
        setLoading(false);
      } catch {
        setLoading(false);
      }
    },
    [
      api,
      filters.media,
      filters.broker,
      filters.client,
      filters.integration,
      filters.request_sent_at,
      filters.origin,
      filters.request_id,
      filters.destination
    ],
  );

  const showDetailsLogModal = useCallback((logData: ILogInteractionRequest) => {
    setSelectedLogData(logData);
    setShowDetailsModal(true);
  }, []);

  const showStatusModal = useCallback((logData: ILogInteractionRequest) => {
    setSelectedLogData(logData);
    loadStatus(logData.external_id);
    setStatusModal(true);
  }, []);

  const getTranslatedStatus = useCallback((status: string): string => {
    return STATUS_TRANSLATIONS[status.toLowerCase()] || status;
  }, []);

  const renderStatusTag = useCallback((status?: string) => {
    if (!status) return <Text type="secondary" />;

    const originalStatusLower = status.toLowerCase();
    const translatedStatus = getTranslatedStatus(status);
    let color = 'default';

    if (originalStatusLower === 'delivered' || originalStatusLower === 'sent' || originalStatusLower === 'read') {
      color = 'success';
    } else if (originalStatusLower === 'failed') {
      color = 'error';
    } else if (originalStatusLower === 'queued') {
      color = 'processing';
    }
    return <Tag color={color}>{translatedStatus}</Tag>;
  }, []);

  const columns = [
    {
      title: 'Status atualizado em:',
      dataIndex: 'webhook_received_at',
      key: 'webhook_received_at',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => renderStatusTag(status),
    },
  ];

  const tableColumns: ITableColumn[] = [
    {
      id: v4(),
      name: 'Executado em',
      dataIndex: 'request_sent_at',
      responsive: false,
    },
    {
      id: v4(),
      name: 'Request ID',
      dataIndex: 'request_id',
      responsive: false,
    },
    {
      id: v4(),
      name: 'Canal',
      dataIndex: 'media',
      responsive: true,
    },
    {
      id: v4(),
      name: 'Broker',
      dataIndex: 'broker',
      responsive: true,
    },
    {
      id: v4(),
      name: 'Cliente',
      dataIndex: 'client',
      responsive: false,
    },
    {
      id: v4(),
      name: 'Integração',
      dataIndex: 'integration',
      responsive: false,
    },
    {
      id: v4(),
      name: 'Origem',
      dataIndex: 'origin',
      responsive: false,
    },
    {
      id: v4(),
      name: 'Destino', 
      dataIndex: 'destination',
      responsive: false,
      renderColumn: (record: ILogInteractionRequest) => {
        const value = record.origin === 'webhook' ? record.sender_id : record.destination;
        return <p>{value || ''}</p>;
      },
    },
    {
      id: v4(),
      name: 'Status',
      dataIndex: 'status',
      responsive: false,
      renderColumn: ({ status }: ILogInteractionRequest) => {
        return <p>{renderStatusTag(status || '')}</p>;
      },
    },
  ];

  const headerActions: IActionHeaderConfig[] = [
    {
      titleAction: 'Filtros',
      icon: <FaFilter />,
      action: () => {
        setShowFilters(true);
      },
    },
    {
      titleAction: 'Atualizar',
      icon: <FaSyncAlt />,
      action: () => {
        loadLogs();
      },
    },
  ];

  const onSearch = useCallback(
    (
      media: string,
      broker: string,
      client: string,
      integration: string,
      request_sent_at: string,
      origin: string,
      request_id: string,
      destination: string,
    ) => {
      setFilters({
        media,
        broker,
        client,
        integration,
        request_sent_at,
        origin,
        request_id,
        destination
      });

      loadLogs(
        1,
        media,
        broker,
        client,
        integration,
        request_sent_at,
        origin,
        request_id,
        destination
      );
      setShowFilters(false);
    },
    [loadLogs],
  );

  return (
    <Card>
      <TableLog
        title="Log Completo das Interações"
        actionsConfig={headerActions}
        data={interactionsRequests}
        keyIndex="id"
        tableColumns={tableColumns}
        tableActions={[
          {
            name: 'Detalhes',
            icon: <EyeOutlined />, 
            action: (data: ILogInteractionRequest) => {
              showDetailsLogModal(data);
            },
            disabled: () => {
              return false;
            },
          },
          {
            name: 'Status',
            icon: <SendOutlined />, 
            action: (data: ILogInteractionRequest) => {
              showStatusModal(data);
            },
            disabled: (data: ILogInteractionRequest) => {
              const shouldDisable = !(
                data.media === 'WHATSAPP' &&
                data.broker === 'FACEBOOK' &&
                data.external_id
              );
              return shouldDisable;
            },
          },
        ]}
        tablePagination={{
          current: pageable.page + 1,
          defaultPageSize: pageable.elementsPerPage,
          totalItems: pageable.totalElements,
        }}
        onChangePage={loadLogs}
        loading={loading}
      />

      <LogInteractionsFilters
        closeFilters={() => setShowFilters(false)}
        showFilters={showFilters}
        search={onSearch}
      />

      <Modal
        title={
          <Title level={4} style={{ margin: 0 }}>
            Detalhes da Execução
          </Title>
        }
        visible={showDetailsModal}
        onCancel={() => setShowDetailsModal(false)}
        footer={null}
        width={800}
      >
        {selectedLogData && (
          <div style={{ padding: '20px' }}>
            <p>
              <strong>Request ID:</strong> {selectedLogData.request_id}
            </p>

            <Title level={5}>Informações Gerais</Title>
            <p>
              <strong>Status:</strong> {renderStatusTag(selectedLogData.status || 'N/A')}
            </p>
            <p>
              <strong>Destino:</strong> {selectedLogData.origin === 'webhook' ? selectedLogData.sender_id : selectedLogData.destination}
            </p>
            <p>
              <strong>Callback URL:</strong> {selectedLogData.callback_url || 'N/A'}
            </p>
            <p>
              <strong>Executado em:</strong> {selectedLogData.request_sent_at || 'N/A'}
            </p>

            <div style={{ display: 'flex', marginTop: '20px' }}>
              <div style={{ flex: 1, marginRight: '10px' }}>
                <Title level={5}>{selectedLogData.origin === 'webhook' ? "Webhook recebido" : "Dados da Requisição"}</Title>
                <Card size="small" style={{ minHeight: '200px', overflow: 'auto' }}>
                  <pre
                    dangerouslySetInnerHTML={{
                      __html: selectedLogData.content
                        ? JSON.stringify(JSON.parse(selectedLogData.content), null, 2)
                        : '<span style="color: grey;">N/A ou JSON inválido</span>',
                    }}
                    style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
                  />
                </Card>
              </div>

              <div style={{ flex: 1, marginLeft: '10px' }}>
                <Title level={5}>{selectedLogData.origin === 'webhook' ? "Callback enviado" : "Dados da Resposta"}</Title>
                <Card size="small" style={{ minHeight: '200px', overflow: 'auto' }}>
                  <pre
                    dangerouslySetInnerHTML={{
                      __html: selectedLogData.callback_message
                        ? JSON.stringify(JSON.parse(selectedLogData.callback_message), null, 2)
                        : '<span style="color: grey;">N/A ou JSON inválido</span>',
                    }}
                    style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
                  />
                </Card>
              </div>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title={
          <Title level={4} style={{ margin: 0 }}>
            Status
          </Title>
        }
        visible={statusModal}
        onCancel={() => setStatusModal(false)}
        footer={null}
        width={800}
      >
        {webhooksRequests.length > 1 ? (
          <Table 
              dataSource={webhooksRequests} 
              columns={columns} 
              rowKey="id" 
              pagination={false}
          />
        ) : (webhooksRequests.length > 0 && (<div style={{ flex: 1, marginLeft: '10px' }}>
                <Title level={5}>{renderStatusTag(webhooksRequests[0].status)}</Title>
                <Card size="small" style={{ minHeight: '200px', overflow: 'auto' }}>
                  <pre
                    dangerouslySetInnerHTML={{
                      __html: webhooksRequests[0].content
                        ? JSON.stringify(JSON.parse(webhooksRequests[0].content), null, 2)
                        : '<span style="color: grey;">N/A ou JSON inválido</span>',
                    }}
                    style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
                  />
                </Card>
              </div>))}
      </Modal>
      
    </Card>
  );
};

export default LogFullInteraction;
